import { useTranslation } from 'react-i18next';
import { Flex } from 'antd';
import { spacing } from '../../../../theme-v2';
import {
  StyledMessage,
  StyledShimmerCover,
  StyledShimmerLine,
  StyledWrapper,
} from './TrackPlayer.styles';
import { TrackPlayer } from './TrackPlayer';
import type { PodcastOnlyProps, TrackAddonProps, TrackKey } from './types';

/**
 * Shared implementation behind `<Podcast>` and `<AudioDoc>` (loading / empty
 * states + the `<TrackPlayer>` itself) — the two only differ by `trackKey`,
 * which drives the gradient + paused icon inside `<TrackPlayer>`. Not part
 * of the public API: `Podcast`/`AudioDoc` stay the exported components so
 * consumers keep their own tab identity.
 */
export const TrackAddon = ({
  trackKey,
  track,
  loading,
  error,
  className,
  analyticEvents,
  autoPlay,
  speedControl,
  initialTime,
  initialSpeed,
  progressEvents,
}: TrackAddonProps &
  PodcastOnlyProps & {
    trackKey: TrackKey;
  }) => {
  const { t } = useTranslation(undefined, { useSuspense: false });

  if (loading) {
    return (
      <StyledWrapper className={className} aria-busy="true">
        <Flex gap={spacing.md} align="center" style={{ padding: spacing.lg }}>
          <StyledShimmerCover />
          <Flex vertical gap={spacing.sm} style={{ flex: 1 }}>
            <StyledShimmerLine $w="40%" />
            <StyledShimmerLine $w="70%" />
            <StyledShimmerLine $w="50%" />
          </Flex>
        </Flex>
        <StyledShimmerLine $w="100%" $h={4} />
      </StyledWrapper>
    );
  }

  if (!track) {
    return (
      <StyledWrapper className={className}>
        <StyledMessage>
          {error ?? t('experienceV2.audio.empty')}
        </StyledMessage>
      </StyledWrapper>
    );
  }

  return (
    <TrackPlayer
      key={track.url}
      trackKey={trackKey}
      track={track}
      t={t}
      analyticEvents={analyticEvents}
      className={className}
      autoPlay={autoPlay}
      speedControl={speedControl}
      initialTime={initialTime}
      initialSpeed={initialSpeed}
      progressEvents={progressEvents}
    />
  );
};
